import React, { useEffect, useState } from 'react';
import { Button } from '../components/ui/button';
import { Card, CardContent } from '../components/ui/card';
import { CheckCircle, Loader2, Package, ArrowRight } from 'lucide-react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import axios from 'axios';
import { toast } from 'sonner';
import { trackPurchase } from '../utils/fbpixel';

const API_URL = process.env.REACT_APP_BACKEND_URL;

const CheckoutSuccess = () => {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [status, setStatus] = useState('checking'); // checking, success, error
  const [orderDetails, setOrderDetails] = useState(null);
  const [attempts, setAttempts] = useState(0);

  const sessionId = searchParams.get('session_id');

  useEffect(() => {
    if (sessionId) {
      checkPaymentStatus();
    } else {
      setStatus('error');
    }
  }, [sessionId]);

  const checkPaymentStatus = async () => {
    const maxAttempts = 5;
    const pollInterval = 2000; // 2 seconds

    if (attempts >= maxAttempts) {
      setStatus('error');
      toast.error('Payment status check timed out');
      return;
    }

    try {
      const response = await axios.get(`${API_URL}/api/stripe/checkout-status/${sessionId}`);
      
      if (response.data.payment_status === 'paid') {
        setStatus('success');
        setOrderDetails(response.data);

        // Meta Pixel — Purchase, deduplicated with the server-side CAPI event
        // via the `fb_event_id` query param embedded in the success URL by
        // the backend at checkout-session creation time.
        const fbEventId = searchParams.get('fb_event_id') || undefined;
        trackPurchase({
          orderId: response.data.order_id,
          orderTotal: response.data.amount,
          eventID: fbEventId,
        });

        // Clear cart after successful payment
        localStorage.removeItem('cart');
        toast.success('Payment successful!');
      } else if (response.data.status === 'expired') {
        setStatus('error');
        toast.error('Payment session expired');
      } else {
        // Continue polling
        setAttempts(prev => prev + 1);
        setTimeout(checkPaymentStatus, pollInterval);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      setAttempts(prev => prev + 1);
      
      if (attempts >= maxAttempts - 1) {
        setStatus('error');
        toast.error('Failed to verify payment');
      } else {
        setTimeout(checkPaymentStatus, pollInterval);
      }
    }
  };

  if (status === 'checking') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center">
        <div className="text-center">
          <Loader2 className="w-16 h-16 text-blue-500 animate-spin mx-auto mb-4" />
          <p className="text-xl text-gray-300">Verifying your payment...</p>
          <p className="text-sm text-gray-500 mt-2">Please wait a moment</p>
        </div>
      </div>
    );
  }

  if (status === 'error') {
    return (
      <div className="min-h-screen bg-black flex items-center justify-center p-6">
        <Card className="bg-gray-900 border-gray-800 max-w-md w-full">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
              <span className="text-3xl">❌</span>
            </div>
            <h1 className="text-2xl font-bold text-white mb-4">Payment Verification Failed</h1>
            <p className="text-gray-400 mb-6">
              We couldn't verify your payment. If you were charged, please contact support.
            </p>
            <div className="flex gap-4">
              <Button
                onClick={() => navigate('/cart')}
                variant="outline"
                className="flex-1"
              >
                Back to Cart
              </Button>
              <Button
                onClick={() => navigate('/contact')}
                className="flex-1 bg-blue-500 hover:bg-blue-600"
              >
                Contact Support
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-black">
      <div className="container mx-auto px-6 py-16">
        <div className="max-w-2xl mx-auto">
          <Card className="bg-gray-900 border-gray-800">
            <CardContent className="p-8">
              {/* Success Icon */}
              <div className="text-center mb-8">
                <div className="w-20 h-20 bg-green-500/20 rounded-full flex items-center justify-center mx-auto mb-4">
                  <CheckCircle className="w-12 h-12 text-green-500" />
                </div>
                <h1 className="text-3xl font-bold text-white mb-2">Payment Successful!</h1>
                <p className="text-gray-400">Thank you for your order</p>
              </div>

              {/* Order Details */}
              {orderDetails && (
                <div className="bg-gray-800 rounded-lg p-6 mb-6">
                  <h2 className="text-lg font-semibold text-white mb-4">Order Details</h2>
                  <div className="space-y-3">
                    <div className="flex justify-between">
                      <span className="text-gray-400">Order ID:</span>
                      <span className="text-white font-mono">{orderDetails.order_id}</span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Total Amount:</span>
                      <span className="text-white font-semibold">
                        ${orderDetails.amount?.toFixed(2)} {orderDetails.currency?.toUpperCase()}
                      </span>
                    </div>
                    <div className="flex justify-between">
                      <span className="text-gray-400">Payment Status:</span>
                      <span className="text-green-500 font-semibold">Paid</span>
                    </div>
                  </div>
                </div>
              )}

              {/* What's Next */}
              <div className="bg-blue-500/10 border border-blue-500/30 rounded-lg p-6 mb-6">
                <div className="flex items-start gap-3">
                  <Package className="w-6 h-6 text-blue-500 flex-shrink-0 mt-1" />
                  <div>
                    <h3 className="text-lg font-semibold text-white mb-2">What's Next?</h3>
                    <ul className="text-gray-300 space-y-2 text-sm">
                      <li>• You'll receive an order confirmation email shortly</li>
                      <li>• We'll start processing your NFC products</li>
                      <li>• Track your order status in your email</li>
                      <li>• Estimated delivery: 3-5 business days</li>
                    </ul>
                  </div>
                </div>
              </div>

              {/* Action Buttons */}
              <div className="flex gap-4">
                <Button
                  onClick={() => navigate('/products')}
                  className="flex-1 bg-blue-500 hover:bg-blue-600 text-white"
                >
                  Continue Shopping
                  <ArrowRight className="w-5 h-5 ml-2" />
                </Button>
                <Button
                  onClick={() => navigate('/')}
                  variant="outline"
                  className="flex-1"
                >
                  Back to Home
                </Button>
              </div>
            </CardContent>
          </Card>
        </div>
      </div>
    </div>
  );
};

export default CheckoutSuccess;
